// src/core/drag.ts
function createDragController(params, pointerEvent) {
  const { initialPointerX, initialPointerY, initialWindowPosition } = params;
  const target = pointerEvent.target;
  const pointerId = pointerEvent.pointerId;
  function onPointerMove(e) {
    const dx = e.clientX - initialPointerX;
    const dy = e.clientY - initialPointerY;
    let x = initialWindowPosition.x + dx;
    let y = initialWindowPosition.y + dy;
    const bounds = typeof params.bounds === "function" ? params.bounds() : params.bounds;
    if (bounds) {
      x = Math.max(0, Math.min(x, bounds.width - params.windowSize.width));
      y = Math.max(0, Math.min(y, bounds.height - params.windowSize.height));
    }
    params.onMove({ x, y });
  }
  function onPointerUp() {
    try {
      target.releasePointerCapture(pointerId);
    } catch {
    }
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    params.onEnd();
  }
  try {
    target.setPointerCapture(pointerId);
  } catch {
  }
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  return function cleanup() {
    try {
      target.releasePointerCapture(pointerId);
    } catch {
    }
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
  };
}

// src/core/events.ts
var Emitter = class {
  listeners = /* @__PURE__ */ new Map();
  changeScheduled = false;
  on(event, handler) {
    let set = this.listeners.get(event);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.listeners.set(event, set);
    }
    const h = handler;
    set.add(h);
    return () => {
      set.delete(h);
    };
  }
  off(event, handler) {
    this.listeners.get(event)?.delete(handler);
  }
  emit(event, payload) {
    const set = this.listeners.get(event);
    if (set) {
      for (const handler of set) {
        handler(payload);
      }
    }
  }
  scheduleChange(getState) {
    if (!this.changeScheduled) {
      this.changeScheduled = true;
      queueMicrotask(() => {
        this.changeScheduled = false;
        this.emit("state:changed", { state: getState() });
      });
    }
  }
  clearListeners() {
    this.listeners.clear();
  }
};

// src/core/id.ts
var counter = 0;
function createWindowId() {
  return `w_${++counter}_${Date.now().toString(36)}`;
}

// src/core/resize.ts
function createResizeController(params, pointerEvent) {
  const {
    edge,
    initialPointerX,
    initialPointerY,
    initialPosition,
    initialSize,
    constraints
  } = params;
  const target = pointerEvent?.target;
  const pointerId = pointerEvent?.pointerId;
  const affectsLeft = edge.includes("w");
  const affectsRight = edge.includes("e");
  const affectsTop = edge.includes("n");
  const affectsBottom = edge.includes("s");
  function onPointerMove(e) {
    const dx = e.clientX - initialPointerX;
    const dy = e.clientY - initialPointerY;
    let x = initialPosition.x;
    let y = initialPosition.y;
    let w = initialSize.width;
    let h = initialSize.height;
    if (affectsRight) {
      w = initialSize.width + dx;
    }
    if (affectsLeft) {
      w = initialSize.width - dx;
      x = initialPosition.x + dx;
    }
    if (affectsBottom) {
      h = initialSize.height + dy;
    }
    if (affectsTop) {
      h = initialSize.height - dy;
      y = initialPosition.y + dy;
    }
    const minW = constraints.minWidth ?? 120;
    const minH = constraints.minHeight ?? 80;
    const maxW = constraints.maxWidth ?? Infinity;
    const maxH = constraints.maxHeight ?? Infinity;
    if (w < minW) {
      if (affectsLeft) {
        x -= minW - w;
      }
      w = minW;
    }
    if (w > maxW) {
      if (affectsLeft) {
        x -= maxW - w;
      }
      w = maxW;
    }
    if (h < minH) {
      if (affectsTop) {
        y -= minH - h;
      }
      h = minH;
    }
    if (h > maxH) {
      if (affectsTop) {
        y -= maxH - h;
      }
      h = maxH;
    }
    const bounds = typeof params.bounds === "function" ? params.bounds() : params.bounds;
    if (bounds) {
      if (x < 0) {
        w += x;
        x = 0;
      }
      if (y < 0) {
        h += y;
        y = 0;
      }
      if (x + w > bounds.width) {
        w = bounds.width - x;
      }
      if (y + h > bounds.height) {
        h = bounds.height - y;
      }
    }
    params.onResize({ x, y }, { width: w, height: h });
  }
  function onPointerUp() {
    if (target && pointerId != null) {
      try {
        target.releasePointerCapture(pointerId);
      } catch {
      }
    }
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    params.onEnd();
  }
  if (target && pointerId != null) {
    try {
      target.setPointerCapture(pointerId);
    } catch {
    }
  }
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  return function cleanup() {
    if (target && pointerId != null) {
      try {
        target.releasePointerCapture(pointerId);
      } catch {
      }
    }
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
  };
}

// src/core/WindowManager.ts
function toReadonly(w) {
  return {
    id: w.id,
    title: w.title,
    position: w.position,
    size: w.size,
    constraints: w.constraints,
    zIndex: w.zIndex,
    focused: w.focused,
    minimized: w.minimized,
    maximized: w.maximized,
    data: w.data
  };
}
var WindowManager = class extends Emitter {
  windows = /* @__PURE__ */ new Map();
  orderList = [];
  nextZIndex;
  cachedState = null;
  activeDragCleanup = null;
  activeResizeCleanup = null;
  options;
  cascadeIndex = 0;
  constructor(options) {
    super();
    this.options = options ?? {};
    this.nextZIndex = this.options.zIndexBase ?? 1;
  }
  getState() {
    if (!this.cachedState) {
      this.cachedState = this.buildState();
    }
    return this.cachedState;
  }
  getWindow(id) {
    return this.getState().windows.get(id);
  }
  getFocusedId() {
    for (const w of this.windows.values()) {
      if (w.focused) {
        return w.id;
      }
    }
    return null;
  }
  open(config) {
    const id = createWindowId();
    const cascade = this.options.cascadeOffset ?? { x: 30, y: 30 };
    const position = {
      x: config.position?.x ?? cascade.x * this.cascadeIndex,
      y: config.position?.y ?? cascade.y * this.cascadeIndex
    };
    this.cascadeIndex++;
    const zIndex = ++this.nextZIndex;
    for (const w of this.windows.values()) {
      w.focused = false;
    }
    const state = {
      id,
      title: config.title ?? "Untitled",
      position,
      size: { ...config.size },
      constraints: config.constraints ? { ...config.constraints } : { ...this.options.defaultConstraints ?? {} },
      zIndex,
      focused: true,
      minimized: false,
      maximized: false,
      preMaximizePosition: null,
      preMaximizeSize: null,
      data: config.data ?? {}
    };
    this.windows.set(id, state);
    this.orderList.push(id);
    this.invalidateCache();
    this.emit("window:created", {
      windowId: id,
      state: toReadonly(state)
    });
    this.emit("window:focused", { windowId: id });
    this.scheduleChange(() => this.getState());
    return id;
  }
  close(id) {
    const win = this.windows.get(id);
    if (!win) {
      return;
    }
    const wasFocused = win.focused;
    this.windows.delete(id);
    this.orderList = this.orderList.filter((wid) => wid !== id);
    this.invalidateCache();
    this.emit("window:closed", { windowId: id });
    if (wasFocused && this.orderList.length > 0) {
      const topId = this.orderList[this.orderList.length - 1];
      this.focusInternal(topId);
    }
    this.scheduleChange(() => this.getState());
  }
  focus(id) {
    const win = this.windows.get(id);
    if (!win || win.focused) {
      return;
    }
    this.focusInternal(id);
    this.scheduleChange(() => this.getState());
  }
  move(id, position) {
    const win = this.windows.get(id);
    if (!win || this.samePosition(win.position, position)) {
      return;
    }
    win.position = position;
    this.invalidateCache();
    this.emit("window:moved", { windowId: id, position });
    this.scheduleChange(() => this.getState());
  }
  resize(id, size, position) {
    const win = this.windows.get(id);
    if (!win) {
      return;
    }
    const sameSize = win.size.width === size.width && win.size.height === size.height;
    const samePos = !position || this.samePosition(win.position, position);
    if (sameSize && samePos) {
      return;
    }
    win.size = size;
    if (position) {
      win.position = position;
    }
    this.invalidateCache();
    this.emit("window:resized", {
      windowId: id,
      size,
      position: win.position
    });
    this.scheduleChange(() => this.getState());
  }
  minimize(id) {
    const win = this.windows.get(id);
    if (!win || win.minimized) {
      return;
    }
    win.minimized = true;
    win.maximized = false;
    this.invalidateCache();
    this.scheduleChange(() => this.getState());
  }
  maximize(id) {
    const win = this.windows.get(id);
    if (!win || win.maximized) {
      return;
    }
    win.preMaximizePosition = { ...win.position };
    win.preMaximizeSize = { ...win.size };
    const bounds = typeof this.options.bounds === "function" ? this.options.bounds() : this.options.bounds;
    if (bounds) {
      win.position = { x: 0, y: 0 };
      win.size = { width: bounds.width, height: bounds.height };
    }
    win.maximized = true;
    win.minimized = false;
    this.invalidateCache();
    this.scheduleChange(() => this.getState());
  }
  restore(id) {
    const win = this.windows.get(id);
    if (!win) {
      return;
    }
    if (win.maximized && win.preMaximizePosition && win.preMaximizeSize) {
      win.position = win.preMaximizePosition;
      win.size = win.preMaximizeSize;
      win.preMaximizePosition = null;
      win.preMaximizeSize = null;
    }
    win.minimized = false;
    win.maximized = false;
    this.invalidateCache();
    this.scheduleChange(() => this.getState());
  }
  toggleMaximize(id) {
    const win = this.windows.get(id);
    if (!win) {
      return;
    }
    if (win.maximized) {
      this.restore(id);
    } else {
      this.maximize(id);
    }
  }
  setTitle(id, title) {
    const win = this.windows.get(id);
    if (!win || win.title === title) {
      return;
    }
    win.title = title;
    this.invalidateCache();
    this.scheduleChange(() => this.getState());
  }
  setData(id, data) {
    const win = this.windows.get(id);
    if (!win) {
      return;
    }
    win.data = { ...win.data, ...data };
    this.invalidateCache();
    this.scheduleChange(() => this.getState());
  }
  closeAll() {
    const focusedId = this.getFocusedId();
    if (focusedId) {
      this.emit("window:blurred", { windowId: focusedId });
    }
    const ids = [...this.orderList];
    for (const id of ids) {
      this.windows.delete(id);
      this.emit("window:closed", { windowId: id });
    }
    this.orderList = [];
    this.invalidateCache();
    this.scheduleChange(() => this.getState());
  }
  startDrag(id, pointerEvent) {
    const win = this.windows.get(id);
    if (!win) {
      return () => {
      };
    }
    this.focus(id);
    this.activeDragCleanup?.();
    if (win.maximized) {
      return () => {
      };
    }
    this.activeDragCleanup = createDragController(
      {
        initialPointerX: pointerEvent.clientX,
        initialPointerY: pointerEvent.clientY,
        initialWindowPosition: { ...win.position },
        windowSize: { ...win.size },
        bounds: this.options.bounds,
        onMove: (position) => this.move(id, position),
        onEnd: () => {
          this.activeDragCleanup = null;
        }
      },
      pointerEvent
    );
    return this.activeDragCleanup;
  }
  startResize(id, edge, pointerEvent) {
    const win = this.windows.get(id);
    if (!win) {
      return () => {
      };
    }
    this.focus(id);
    this.activeResizeCleanup?.();
    this.activeResizeCleanup = createResizeController(
      {
        edge,
        initialPointerX: pointerEvent.clientX,
        initialPointerY: pointerEvent.clientY,
        initialPosition: { ...win.position },
        initialSize: { ...win.size },
        constraints: win.constraints,
        bounds: this.options.bounds,
        onResize: (position, size) => this.resize(id, size, position),
        onEnd: () => {
          this.activeResizeCleanup = null;
        }
      },
      pointerEvent
    );
    return this.activeResizeCleanup;
  }
  destroy() {
    this.activeDragCleanup?.();
    this.activeResizeCleanup?.();
    this.windows.clear();
    this.orderList = [];
    this.cachedState = null;
    this.clearListeners();
  }
  samePosition(a, b) {
    return a.x === b.x && a.y === b.y;
  }
  focusInternal(id) {
    const prevFocusedId = this.getFocusedId();
    for (const w of this.windows.values()) {
      w.focused = w.id === id;
    }
    const win = this.windows.get(id);
    if (win) {
      win.zIndex = ++this.nextZIndex;
    }
    this.invalidateCache();
    if (prevFocusedId && prevFocusedId !== id) {
      this.emit("window:blurred", { windowId: prevFocusedId });
    }
    this.emit("window:focused", { windowId: id });
  }
  invalidateCache() {
    this.cachedState = null;
  }
  buildState() {
    const windowMap = /* @__PURE__ */ new Map();
    for (const [id, w] of this.windows) {
      windowMap.set(id, toReadonly(w));
    }
    return {
      windows: windowMap,
      order: [...this.orderList],
      focusedId: this.getFocusedId()
    };
  }
};

// src/index.ts
function createWindowManager(options) {
  return new WindowManager(options);
}
export {
  WindowManager,
  createWindowManager
};
